import { pushConversionOnce } from '../../utils/conversion-tracking.js';
import { translate } from '../../i18n/index.js';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from '../../components/design/AtomFitUI.js';
import { bookingsApi } from '../../api/bookings.api.js';
import { useBookingStore } from '../../store/booking.store.js';
import type { BookingCalendarEvent } from '../../utils/calendar.js';
import type { CustomerBooking } from '../../api/bookings.api.js';
import { downloadBookingIcs } from '../../utils/calendar.js';
import { ConfirmationMark } from '../../components/common/ConfirmationMark.js';

export function BookingConfirm() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const reset = useBookingStore((state) => state.reset);
  const [calendarEvent, setCalendarEvent] = useState<BookingCalendarEvent | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(Boolean(bookingId));
  const [calendarError, setCalendarError] = useState<string | null>(
    bookingId ? null : translate('pages.public.booking_confirm.booking_confirm.use_state.booking'),
  );

  useEffect(() => {
    if (!bookingId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    setCalendarLoading(true);
    setCalendarError(null);

    const load = () => Promise.all([bookingsApi.get(bookingId), bookingsApi.my()])
      .then(([response, bookingsResponse]) => {
        if (cancelled) return;
        const booking = response.data;
        if (booking.status === 'PENDING') {
          timer = setTimeout(() => void load(), 1000);
          return;
        }
        const slotRange = getAdjacentBookingRange(
          booking,
          bookingsResponse.data.data,
          booking.gym.slotBufferMinutes,
        );
        if (['PAID', 'ACTIVE', 'COMPLETED'].includes(booking.status) &&
          (!booking.conversionPayment || booking.conversionPayment.status === 'SUCCEEDED')) {
          const price = (booking.conversionPayment?.amountInSmallestUnit ??
            slotRange.reduce((sum, item) => sum + item.priceInSmallestUnit, 0)) / 100;
          const duration = slotRange.reduce((sum, item) => sum +
            (new Date(item.slot.endTime).getTime() - new Date(item.slot.startTime).getTime()) / 60_000, 0);
          pushConversionOnce({
            event: 'booking_confirmed',
            booking_id: booking.id,
            price,
            currency: booking.conversionPayment?.currency ?? booking.currency,
            payment_status: price > 0 ? 'paid'
              : slotRange.some((item) => item.creditAppliedInSmallestUnit > 0) ? 'credit'
              : slotRange.some((item) => item.promoRedemptionId || item.referralCreditId) ? 'code' : 'free',
            product: duration > 30 ? 'slot_60' : 'slot_30',
            duration_min: duration,
          }, booking.bookingGroupId ?? booking.id);
        }
        setCalendarEvent({
          bookingId: slotRange[0]?.id ?? booking.id,
          address: [booking.gym.addressLine1, booking.gym.postcode, booking.gym.city]
            .filter(Boolean)
            .join(', '),
          startTime: slotRange[0]?.slot.startTime ?? booking.slot.startTime,
          endTime: slotRange[slotRange.length - 1]?.slot.endTime ?? booking.slot.endTime,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCalendarError(
            translate(
              'pages.public.booking_confirm.booking_confirm.set_calendar_error.error_calendar',
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCalendarLoading(false);
      });

    void load();
    return () => {
      clearTimeout(timer);
      cancelled = true;
    };
  }, [bookingId]);

  useEffect(() => {
    return () => reset();
  }, []);

  return (
    <main className="af-screen af-booking-confirm-screen">
      <section
        className="af-shell af-booking-confirm-shell"
        aria-labelledby="booking-confirm-title"
      >
        <ConfirmationMark />

        <h1 id="booking-confirm-title" className="af-confirm-title">
          {translate(
            'pages.public.booking_confirm.booking_confirm.h1_confirm_title.booking_confirmation',
          )}
        </h1>

        <div className="af-confirm-actions">
          <button
            className="af-outline-btn"
            type="button"
            onClick={() => calendarEvent && downloadBookingIcs(calendarEvent)}
            disabled={!calendarEvent || calendarLoading}
          >
            {calendarLoading ? <Spinner /> : <CalendarIcon />}
            {calendarLoading
              ? translate('pages.public.booking_confirm.booking_confirm.button_outline_btn.loading')
              : translate(
                  'pages.public.booking_confirm.booking_confirm.button_outline_btn.calendar_add',
                )}
          </button>
          <Link className="af-outline-btn" to="/reservations" style={{ textDecoration: 'none' }}>
            <ListIcon />
            {translate('pages.public.booking_confirm.booking_confirm.link_outline_btn.booking')}
          </Link>
        </div>

        <Link className="af-confirm-home-link" to="/">
          {translate('pages.public.booking_confirm.booking_confirm.link_confirm_home_link.back')}
        </Link>
        {calendarError && <div className="af-confirm-error">{calendarError}</div>}
      </section>
    </main>
  );
}

type BookingCalendarSource = Pick<
  CustomerBooking,
  'id' | 'gymId' | 'bookingGroupId' | 'status' | 'slot' | 'priceInSmallestUnit' | 'creditAppliedInSmallestUnit' | 'promoRedemptionId' | 'referralCreditId'
>;

function getAdjacentBookingRange(
  booking: BookingCalendarSource,
  bookings: BookingCalendarSource[],
  bufferMinutes: number,
) {
  if (!booking.bookingGroupId) return [booking];

  const groupedBookings = bookings
    .filter(
      (item) =>
        item.bookingGroupId === booking.bookingGroupId &&
        item.gymId === booking.gymId &&
        ['PAID', 'ACTIVE', 'COMPLETED'].includes(item.status),
    )
    .sort((a, b) => new Date(a.slot.startTime).getTime() - new Date(b.slot.startTime).getTime());
  const targetIndex = groupedBookings.findIndex((item) => item.id === booking.id);
  if (targetIndex === -1) return [booking];

  const bufferMilliseconds = Math.max(0, bufferMinutes) * 60_000;
  let firstIndex = targetIndex;
  let lastIndex = targetIndex;

  while (
    firstIndex > 0 &&
    new Date(groupedBookings[firstIndex]!.slot.startTime).getTime() ===
      new Date(groupedBookings[firstIndex - 1]!.slot.endTime).getTime() + bufferMilliseconds
  ) {
    firstIndex -= 1;
  }
  while (
    lastIndex < groupedBookings.length - 1 &&
    new Date(groupedBookings[lastIndex + 1]!.slot.startTime).getTime() ===
      new Date(groupedBookings[lastIndex]!.slot.endTime).getTime() + bufferMilliseconds
  ) {
    lastIndex += 1;
  }

  return groupedBookings.slice(firstIndex, lastIndex + 1);
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.5" y="3" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.5" cy="10" r="1" fill="currentColor" />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
      <circle cx="10.5" cy="10" r="1" fill="currentColor" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M5.5 4h8M5.5 8h8M5.5 12h8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      <circle cx="2.5" cy="4" r="1" fill="currentColor" />
      <circle cx="2.5" cy="8" r="1" fill="currentColor" />
      <circle cx="2.5" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}
